import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'

import { logger } from '@nx/utils/logger'

import { TypesenseClientInstance } from '../typesenseClient'
import {
  GetTypesenseAuctionsParams,
  getTypesenseAuctions,
} from './getTypesenseAuctions'

type UseGetTypesenseAuctionsProps = {
  client: TypesenseClientInstance
  auctionsPerPage: number
  searchParams: GetTypesenseAuctionsParams & { startingPage: number }
}

export function useGetTypesenseAuctions({
  client,
  auctionsPerPage,
  searchParams: { startingPage, ...getTypesenseAuctionsParams },
}: UseGetTypesenseAuctionsProps) {
  const result = useInfiniteQuery({
    queryKey: ['auctions', getTypesenseAuctionsParams],
    queryFn: ({ pageParam: page }) =>
      getTypesenseAuctions(client)({
        ...getTypesenseAuctionsParams,
        page,
        per_page: auctionsPerPage,
      }),
    getNextPageParam: ({ page, nbPages }) =>
      page < nbPages ? page + 1 : undefined,
    getPreviousPageParam: (_, __, firstPageParam) =>
      typeof firstPageParam !== 'undefined' && firstPageParam > 1
        ? firstPageParam - 1
        : undefined,
    staleTime: 0,
    initialPageParam: startingPage,
    placeholderData: keepPreviousData,
    retry: 3,
    retryDelay: 1000,
  })

  if (result.isError) {
    logger.error(
      result.error,
      `auctions collection - auctionIds: ${JSON.stringify(getTypesenseAuctionsParams)}`
    )
  }

  return result
}
